import { css } from '@emotion/react'
import styled from '@emotion/styled'
import React from 'react'
import {
  LeshenPhoneCTA,
  PackageCard,
  useMapi,
  UserPackages,
} from '@leshen/gatsby-theme-leshen'
import {
  Columns,
  LinkButton,
  Stack,
  Typography,
  VariableContent,
  scrollTo,
} from '@leshen/ui'
import zipLogic from '../utils/zipLogic'
import Spinner from './Spinner'

function ZipPackageSection({
  spanish = false,
  disableGeo = false,
  modal = null
}) {
  const { rotatedNumber } = useMapi()
  return (
    <UserPackages
      disableGeo={disableGeo}
      scrollTarget="#loadingScrollTarget"
      exec={(loc) => zipLogic(loc.zipCode, spanish)}
      component={({ data, location, loading }) => {
        const service = data?.typeOfService || ''
        const hasPkgs = !loading && data?.getQFiberPackages?.length >= 1
        const showQfSection = hasPkgs && service === 'QF'
        const showCtlSection = hasPkgs && service === 'CTL'
        const showOoaSection = !hasPkgs && service === 'OOA'
        return (
          <>
            {loading && (
              <VariableContent
                className="packages"
                alignMainContent="center"
                mainContent={
                  <>
                    <Typography variant="h2">
                      {spanish
                        ? 'Buscando paquetes en tu zona.'
                        : 'Searching packages in your area.'}
                    </Typography>
                    <Spinner />
                  </>
                }
              />
            )}
            {showQfSection && (
              <VariableContent
                className="packages"
                alignMainContent="center"
                mainContent={
                  <div>
                    <Typography variant="h2">
                      {spanish
                        ? 'Planes y precios de internet de Quantum Fiber'
                        : 'Quantum Fiber Internet Plans'}
                    </Typography>
                    <Typography>
                      {spanish
                        ? 'Puede ser difícil encontrar el plan de Internet adecuado. Así que te lo ponemos fácil, reuniendo todas las velocidades de Internet disponibles en tu área en un solo lugar. Haz tu elección.'
                        : 'It can be difficult to find the right internet plan. So we’ve made it easy for you, gathering all the available internet speeds in your area in one place. Make your choice.'}
                    </Typography>
                    {location.city && location.zipCode && (
                      <Typography>
                        {spanish
                          ? 'Mostrando el mejor plan(es) para'
                          : 'Showing best plan(s) for'}{' '}
                        {location.city}, {}
                        {location.zipCode}{' '}
                        <StyledButtonLink
                          type="button"
                          onClick={() => {
                            scrollTo('h1')
                          }}
                        >
                          {spanish ? 'cambiar' : 'change'}
                        </StyledButtonLink>
                      </Typography>
                    )}
                  </div>
                }
              >
                <Stack spacing="xxl" alignMainContent="center">
                  <Columns>
                    {data.getQFiberPackages.map((pkg) => (
                      <PackageCard
                        key={pkg.label}
                        label={pkg.label}
                        packageData={pkg.brandy}
                        content={
                          <LeshenPhoneCTA color="primary">
                            {spanish ? 'Llama al' : 'Call'}
                          </LeshenPhoneCTA>
                        }
                        modal={modal}
                      />
                    ))}
                  </Columns>
                </Stack>
              </VariableContent>
            )}
            {showCtlSection && (
              <VariableContent
                alignMainContent="center"
                mainContent={
                  <div>
                    <Typography variant="h6">
                      <strong>
                        {spanish
                          ? 'Lo sentimos. Parece que no estás en el área de servicio de Internet de Quantum Fiber.'
                          : 'We’re sorry, Quantum Fiber isn’t available there.'}
                      </strong>
                    </Typography>
                    <Typography variant="h2">
                      Good news! We found other plans in your area
                    </Typography>
                    <StyledButtonsContainer>
                      {data.centurylinkService && (
                        <>
                          <LinkButton
                            variant="feature"
                            className="view-plans-button"
                            to={`https://centurylinkquote.com/?getqfiberZip=${location.zipCode}`}
                          >
                            {spanish ? 'Ver Planes' : 'View Plans'}
                          </LinkButton>
                          <LinkButton
                            variant="feature"
                            to={`tel:${rotatedNumber}`}
                          >
                            {spanish ? 'Llama al ' : 'Call '}{' '}
                            <span className="inline-block">
                              {rotatedNumber}
                            </span>
                            {spanish
                              ? ' para más opciones'
                              : ' for more options'}
                          </LinkButton>
                        </>
                      )}
                    </StyledButtonsContainer>
                  </div>
                }
              />
            )}
            {showOoaSection && (
              <VariableContent
                mainContent={
                  <>
                    <Typography variant="h2">
                      {spanish
                        ? 'Estamos teniendo problemas para encontrar opciones de servicio para tu área.'
                        : 'We\'re having trouble locating service options for your area.'}
                    </Typography>
                    <p>
                      {spanish
                        ? '¡Llámanos y uno de nuestros especialistas en Internet puede ayudar a conectarte!'
                        : 'Give us a call and one of our Internet specialists can help get you connected!'}
                    </p>
                  </>
                }
                alignMainContent="center"
              />
            )}
          </>
        )
      }}
    />
  )
}

export default ZipPackageSection

const StyledButtonLink = styled.button(
  ({ theme }) => css`
    color: ${theme.colors.primary.base.hex};
    cursor: pointer;
    background: none;
    border: none;
    padding: 0;
    display: inline;
    font-weight: bold;
  `,
)

const StyledButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  .view-plans-button {
    margin-right: 16px;
  }
`
